// client/src/pages/wholesaler/WholesalerProfile.js

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ServiceAreaSelector from '../../components/common/ServiceAreaSelector';
import LanguageSelector from '../../components/common/LanguageSelector';
import EmailPreferences from '../../components/common/EmailPreferences';
import PasswordField from '../../components/common/PasswordField';
import { wholesalerAPI, registryAPI } from '../../services/api';
// ✅ FIX: Import image helper
import { getImageUrl } from '../../utils/imageHelper';

// ERP Connectivity Section (Enterprise only)
const ErpConnectivitySection = () => {
  const { t } = useTranslation('wholesaler');
  const [erp, setErp] = useState({
    isActive: false, serverUrl: '', username: '', password: '',
    appId: '', companyId: '', branchId: '1', refId: '1', seriesCode: '7', calcSeriesCode: '', defaultCustomer: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    wholesalerAPI.getErpSettings().then(res => {
      if (res.data?.data) setErp(res.data.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await wholesalerAPI.updateErpSettings(erp);
      setTestResult({ success: true, message: t('erp.saved', 'Settings saved') });
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.message || 'Save failed' });
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const res = await wholesalerAPI.testErpConnection();
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.message || 'Connection failed' });
    } finally { setTesting(false); }
  };

  if (loading) return <div className="mb-8 p-6 bg-gray-50 rounded-xl animate-pulse h-32" />;

  const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm";

  return (
    <div className="mb-8 p-6 bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-purple-900">{t('erp.title', 'ERP Connectivity')}</h3>
          <p className="text-sm text-purple-700">{t('erp.description', 'Connect to SoftOne ERP for automatic order sync')}</p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <span className="text-sm font-medium text-purple-800">
            {erp.isActive ? t('erp.active', 'Active') : t('erp.inactive', 'Inactive')}
          </span>
          <div className="relative">
            <input type="checkbox" className="sr-only" checked={erp.isActive}
              onChange={async (e) => {
                const newState = { ...erp, isActive: e.target.checked };
                setErp(newState);
                try {
                  await wholesalerAPI.updateErpSettings(newState);
                } catch (err) {
                  setErp({ ...newState, isActive: !e.target.checked });
                }
              }} />
            <div className={`w-11 h-6 rounded-full transition-colors ${erp.isActive ? 'bg-purple-600' : 'bg-gray-300'}`}>
              <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform mt-0.5 ${erp.isActive ? 'translate-x-5 ml-0.5' : 'translate-x-0.5'}`} />
            </div>
          </div>
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('erp.serverUrl', 'Server URL')}</label>
          <input className={inputClass} value={erp.serverUrl} placeholder="https://erp.example.com"
            onChange={(e) => setErp({ ...erp, serverUrl: e.target.value })} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('erp.username', 'Username')}</label>
          <input className={inputClass} value={erp.username}
            onChange={(e) => setErp({ ...erp, username: e.target.value })} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('erp.password', 'Password')}</label>
          <input className={inputClass} type="password" value={erp.password}
            onChange={(e) => setErp({ ...erp, password: e.target.value })} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('erp.appId', 'App ID')}</label>
          <input className={inputClass} value={erp.appId}
            onChange={(e) => setErp({ ...erp, appId: e.target.value })} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('erp.companyId', 'Company ID')}</label>
          <input className={inputClass} value={erp.companyId}
            onChange={(e) => setErp({ ...erp, companyId: e.target.value })} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('erp.branchId', 'Branch ID')}</label>
          <input className={inputClass} value={erp.branchId}
            onChange={(e) => setErp({ ...erp, branchId: e.target.value })} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('erp.refId', 'REFID')}</label>
          <input className={inputClass} value={erp.refId} placeholder="1"
            onChange={(e) => setErp({ ...erp, refId: e.target.value })} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('erp.seriesCode', 'Series Code (setData)')}</label>
          <input className={inputClass} value={erp.seriesCode}
            onChange={(e) => setErp({ ...erp, seriesCode: e.target.value })} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('erp.calcSeriesCode', 'Calc Series Code')}</label>
          <input className={inputClass} value={erp.calcSeriesCode} placeholder={erp.seriesCode || 'Same as Series Code'}
            onChange={(e) => setErp({ ...erp, calcSeriesCode: e.target.value })} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('erp.defaultCustomer', 'Default Customer (TRDR)')}</label>
          <input className={inputClass} value={erp.defaultCustomer}
            onChange={(e) => setErp({ ...erp, defaultCustomer: e.target.value })} />
        </div>
      </div>

      {testResult && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${testResult.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {testResult.message}
        </div>
      )}

      <div className="flex gap-3">
        <button onClick={handleTest} disabled={testing || !erp.serverUrl}
          className="px-4 py-2 bg-white border-2 border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 disabled:opacity-50 font-medium text-sm">
          {testing ? '...' : t('erp.testConnection', 'Test Connection')}
        </button>
        <button onClick={handleSave} disabled={saving}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium text-sm">
          {saving ? t('profile.saving') : t('profile.save')}
        </button>
      </div>
    </div>
  );
};

const WholesalerProfile = () => {
  const { t, i18n } = useTranslation('wholesaler');
  const [wholesalerTypes, setWholesalerTypes] = useState([]);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const lang = i18n.language?.startsWith('el') ? 'el' : 'en';
        const res = await registryAPI.getWholesalerCategories(lang);
        setWholesalerTypes(res.data?.data || []);
      } catch { /* silent fallback */ }
    };
    fetchTypes();
  }, [i18n.language]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [userLanguage, setUserLanguage] = useState('el');

  const [formData, setFormData] = useState({
    companyName: '',
    legalName: '',
    vatNumber: '',
    taxOffice: '',
    businessType: '',
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    phone: '',
    email: '',
    contactPerson: '',
    description: '',
    website: '',
    serviceAreas: [],
    freeDeliveryThreshold: 0,
    deliveryFee: 0,
    minimumOrder: 0,
    estimatedDeliveryDays: 1,
    deliveryNotes: '',
    privateCatalogMode: false,  // ✅ PHASE 6: Add this line
    simpleOrdersEnabled: false
  });
  const [erpActive, setErpActive] = useState(false);

  const [subscriptionTier, setSubscriptionTier] = useState('starter');
  const [lockedFields, setLockedFields] = useState([]);
  const [customSlug, setCustomSlug] = useState('');
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [slugChecking, setSlugChecking] = useState(false);
  const [hideFromMarketplace, setHideFromMarketplace] = useState(false);
  const [hidePricing, setHidePricing] = useState(false);

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await wholesalerAPI.getProfile();
      setProfile(response.data);
      
      const wholesaler = response.data.wholesaler;
      setFormData({
        companyName: wholesaler.companyName || '',
        legalName: wholesaler.legalName || '',
        vatNumber: wholesaler.vatNumber || '',
        taxOffice: wholesaler.taxOffice || '',
        businessType: wholesaler.businessType || '',
        street: wholesaler.address?.street || '',
        city: wholesaler.address?.city || '',
        state: wholesaler.address?.state || '',
        postalCode: wholesaler.address?.postalCode || '',
        country: wholesaler.address?.country || 'Greece',
        phone: wholesaler.contact?.phone || '',
        email: wholesaler.contact?.email || '',
        contactPerson: wholesaler.contact?.contactPerson || '',
        description: wholesaler.description || '',
        website: wholesaler.website || '',
        serviceAreas: wholesaler.serviceAreas || [],
        freeDeliveryThreshold: wholesaler.deliverySettings?.freeDeliveryThreshold || 0,
        deliveryFee: wholesaler.deliverySettings?.deliveryFee || 0,
        minimumOrder: wholesaler.deliverySettings?.minimumOrder || 0,
        estimatedDeliveryDays: wholesaler.deliverySettings?.estimatedDeliveryDays || 1,
        deliveryNotes: wholesaler.deliverySettings?.deliveryNotes || '',
        privateCatalogMode: wholesaler.privateCatalogMode || false,  // ✅ PHASE 6: Add this line
        simpleOrdersEnabled: wholesaler.simpleOrdersEnabled || false
      });
      setErpActive(wholesaler.erpIntegration?.isActive === true);

      setSubscriptionTier(wholesaler.subscriptionTier || 'starter');
      setLockedFields(response.data.lockedFields || []);
      setCustomSlug(wholesaler.customSlug || '');
      setHideFromMarketplace(wholesaler.hideFromMarketplace || false);
      setHidePricing(wholesaler.hidePricing || false);

      if (wholesaler.logo) {
        // ✅ FIX: Use getImageUrl to handle Cloudinary vs Local paths correctly
        setLogoPreview(getImageUrl(wholesaler.logo));
      }
      setUserLanguage(response.data.user?.language || 'el');
    } catch (err) {
      setError(t('profile.error'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

   const handlePrivateCatalogToggle = async (enabled) => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      await wholesalerAPI.togglePrivateCatalogMode(enabled);

      setFormData({
        ...formData,
        privateCatalogMode: enabled
      });

      setSuccess(enabled 
        ? 'Private catalog mode enabled. New businesses must request access.'
        : 'Private catalog mode disabled. All businesses can view your catalog.'
      );
      
      setTimeout(() => setSuccess(''), 5000);

    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update private catalog mode');
      console.error('Toggle private mode error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSimpleOrdersToggle = async (enabled) => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      await wholesalerAPI.toggleSimpleOrdersMode(enabled);

      setFormData(prev => ({ ...prev, simpleOrdersEnabled: enabled }));

      setSuccess(enabled
        ? t('simpleOrders.toggle.successOn', 'Simple Orders mode enabled. New orders will use the simplified workflow.')
        : t('simpleOrders.toggle.successOff', 'Simple Orders mode disabled. New orders return to the full workflow.')
      );
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update Simple Orders mode');
      console.error('Toggle simple orders error:', err);
    } finally {
      setSaving(false);
    }
  };

  // ── Enterprise Storefront Handlers ──
  const handleSlugChange = async (value) => {
    const clean = value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 50);
    setCustomSlug(clean);
    setSlugAvailable(null);
    
    if (clean.length >= 3) {
      setSlugChecking(true);
      try {
        const res = await wholesalerAPI.checkSlugAvailability(clean);
        setSlugAvailable(res.data.available);
      } catch {
        setSlugAvailable(null);
      } finally {
        setSlugChecking(false);
      }
    }
  };

  const handleSaveSlug = async () => {
    try {
      setSaving(true);
      setError('');
      await wholesalerAPI.setCustomSlug(customSlug || null);
      setSuccess(customSlug ? t('storefront.slugSaved', 'Custom URL saved!') : t('storefront.slugCleared', 'Custom URL cleared'));
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save custom URL');
    } finally {
      setSaving(false);
    }
  };

  const handleStorefrontToggle = async (field, value) => {
    try {
      setSaving(true);
      setError('');
      // Save via profile update
      await wholesalerAPI.updateProfile({ [field]: value });
      if (field === 'hideFromMarketplace') setHideFromMarketplace(value);
      if (field === 'hidePricing') setHidePricing(value);
      setSuccess(t('storefront.settingSaved', 'Setting updated'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      // FileReader creates a data URL, which is safe for CSP
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const data = new FormData();
      
      // Basic info
      data.append('companyName', formData.companyName);
      data.append('legalName', formData.legalName);
      data.append('vatNumber', formData.vatNumber);
      data.append('taxOffice', formData.taxOffice);
      
      data.append('businessType', formData.businessType);
      data.append('description', formData.description);
      data.append('website', formData.website);
      
      // Address
      data.append('address', JSON.stringify({
        street: formData.street,
        city: formData.city,
        state: formData.state,
        postalCode: formData.postalCode,
        country: formData.country
      }));
      
      // Contact
      data.append('contact', JSON.stringify({
        phone: formData.phone,
        email: formData.email,
        contactPerson: formData.contactPerson
      }));

      // Service Areas
      data.append('serviceAreas', JSON.stringify(formData.serviceAreas));

      // Delivery Settings
      data.append('deliverySettings', JSON.stringify({
        freeDeliveryThreshold: parseFloat(formData.freeDeliveryThreshold) || 0,
        deliveryFee: parseFloat(formData.deliveryFee) || 0,
        minimumOrder: parseFloat(formData.minimumOrder) || 0,
        estimatedDeliveryDays: parseInt(formData.estimatedDeliveryDays) || 1,
        deliveryNotes: formData.deliveryNotes
      }));
      
      // Logo
      if (logoFile) {
        data.append('logo', logoFile);
      }

      await wholesalerAPI.updateProfile(data);
      setSuccess(t('profile.saved'));
      
      // Refresh profile data
      fetchProfile();
    } catch (err) {
      setError(err.response?.data?.message || t('profile.error'));
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError(t('profile.passwordMismatch'));
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError(t('profile.passwordError'));
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      await wholesalerAPI.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      setSuccess(t('profile.passwordChanged'));
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      setError(err.response?.data?.message || t('profile.passwordError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="container mx-auto px-4 py-8 pt-16 lg:pt-8 max-w-4xl">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('profile.title')}</h1>

        {/* Success/Error Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
            {success}
          </div>
        )}

        {/* Language Selector */}
        <LanguageSelector 
          userType="wholesaler"
          currentLanguage={userLanguage}
          onLanguageChange={setUserLanguage}
        />

        {/* Email Preferences */}
        <EmailPreferences userRole="wholesaler" />

        {/* Profile Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">{t('profile.companyInfo')}</h2>

          {/* Logo Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('profile.logo')}
            </label>
            <div className="flex items-center space-x-4">
              {logoPreview && (
                <img
                  src={logoPreview}
                  alt={t('profile.logo')}
                  className="h-20 w-20 object-cover rounded-lg border-2 border-gray-200"
                  // ✅ FIX: Prevent infinite flicker loop
                  onError={(e) => {
                    if (!e.target.dataset.errorHandled) {
                      e.target.dataset.errorHandled = 'true';
                      e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect fill='%23f3f4f6' width='100' height='100'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='14' fill='%239ca3af'%3E?%3C/text%3E%3C/svg%3E";
                    }
                  }}
                />
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Company Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('profile.companyName')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                required
                disabled={lockedFields.includes('companyName')}
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${lockedFields.includes('companyName') ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
              />
              {lockedFields.includes('companyName') && (
                <p className="text-xs text-gray-400 mt-1">{t('profile.fieldLocked', 'Cannot be changed after account approval')}</p>
              )}
            </div>

            {/* Business Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('profile.businessType')} <span className="text-red-500">*</span>
              </label>
              <select
                name="businessType"
                value={formData.businessType}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">{t('profile.selectBusinessType', 'Select type...')}</option>
                {wholesalerTypes.map(type => (
                  <option key={type.key} value={type.key}>
                    {type.icon} {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Website */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('profile.website')}
              </label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Legal Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Legal Name / Επωνυμία
              </label>
              <input
                type="text"
                name="legalName"
                value={formData.legalName}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* VAT Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                VAT Number (ΑΦΜ) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="vatNumber"
                value={formData.vatNumber}
                onChange={handleChange}
                disabled={lockedFields.includes('vatNumber')}
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${lockedFields.includes('vatNumber') ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-gray-50'}`}
              />
              {lockedFields.includes('vatNumber') && (
                <p className="text-xs text-gray-400 mt-1">{t('profile.fieldLocked', 'Cannot be changed after account approval')}</p>
              )}
            </div>

            {/* Tax Office */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tax Office (ΔΟΥ) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="taxOffice"
                value={formData.taxOffice}
                onChange={handleChange}
                disabled={lockedFields.includes('taxOffice')}
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${lockedFields.includes('taxOffice') ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
              />
              {lockedFields.includes('taxOffice') && (
                <p className="text-xs text-gray-400 mt-1">{t('profile.fieldLocked', 'Cannot be changed after account approval')}</p>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('profile.description')}
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              placeholder={t('profile.descriptionPlaceholder')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Address Section */}
          <h3 className="text-lg font-semibold text-gray-900 mb-4 mt-8">{t('profile.address')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('profile.street')}
              </label>
              <input
                type="text"
                name="street"
                value={formData.street}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('profile.city')}
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('profile.state')}
              </label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('profile.country')}
              </label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('profile.postalCode')}
              </label>
              <input
                type="text"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Contact Section */}
          <h3 className="text-lg font-semibold text-gray-900 mb-4 mt-8">{t('profile.contactInfo')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('profile.phone')}
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('profile.email')}
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('profile.contactPerson')}
              </label>
              <input
                type="text"
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Service Areas Section */}
          <h3 className="text-lg font-semibold text-gray-900 mb-4 mt-8 border-t pt-6">
            {t('profile.deliverySettings')}
          </h3>
          
          <div className="mb-6">
            <ServiceAreaSelector
              mode="multiple"
              value={formData.serviceAreas}
              onChange={(areas) => setFormData({ ...formData, serviceAreas: areas })}
              label={t('profile.serviceAreas')}
              showSpecialOptions={true}
            />
          </div>
          
          {/* ✅ PHASE 6: Private Catalog Mode Toggle */}
          <div className="mb-8 p-6 bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {t('catalog.privateModeTitle')}
                  </h3>
                  {formData.privateCatalogMode && (
                    <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded-full">
                      {t('catalog.privateModeEnabled')}
                    </span>
                  )}
                </div>
                
                <p className="text-sm text-gray-700 mb-3">
                  {t('catalog.privateModeDescription')}
                </p>

                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li className="flex items-start">
                    <svg className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>{t('catalog.privateModeReason1')}</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>{t('catalog.privateModeReason2')}</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>{t('catalog.privateModeReason3')}</span>
                  </li>
                </ul>

                {formData.privateCatalogMode && subscriptionTier !== 'starter' && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800 font-medium">
                      📋 {t('catalog.manageRequests')} <a href="/wholesaler/catalog-requests" className="underline hover:text-blue-900">{t('catalog.catalogRequestsLink')}</a> {t('catalog.page')}
                    </p>
                  </div>
                )}

                {subscriptionTier === 'starter' && (
                  <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <p className="text-sm text-purple-800 font-medium mb-2">
                      🔒 {t('catalog.upgradeForPrivate', 'This feature requires a Professional or Enterprise plan.')}
                    </p>
                    <a href="/wholesaler/pricing" className="inline-flex items-center text-sm font-medium text-purple-700 hover:text-purple-900 underline">
                      {t('catalog.viewPlans', 'View Plans & Upgrade →')}
                    </a>
                  </div>
                )}
              </div>

              <div className="ml-6">
                {subscriptionTier === 'starter' ? (
                  <div className="text-center">
                    <div className="relative inline-flex h-10 w-20 flex-shrink-0 rounded-full border-2 border-transparent bg-gray-200 opacity-50 cursor-not-allowed">
                      <span className="pointer-events-none inline-block h-9 w-9 transform rounded-full bg-white shadow ring-0 translate-x-0" />
                    </div>
                    <p className="text-xs text-purple-600 mt-2 font-medium">
                      {t('catalog.proFeature', 'Professional+')}
                    </p>
                  </div>
                ) : (
                <button
                  type="button"
                  onClick={() => handlePrivateCatalogToggle(!formData.privateCatalogMode)}
                  disabled={saving}
                  className={`relative inline-flex h-10 w-20 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    formData.privateCatalogMode ? 'bg-orange-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-9 w-9 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      formData.privateCatalogMode ? 'translate-x-10' : 'translate-x-0'
                    }`}
                  />
                </button>
                )}
                {subscriptionTier !== 'starter' && (
                <p className="text-xs text-gray-500 mt-2 text-center">
                  {formData.privateCatalogMode ? t('catalog.toggleOn') : t('catalog.toggleOff')}
                </p>
                )}
              </div>
            </div>
          </div>

          {/* ================================================================ */}
          {/* SIMPLE ORDERS MODE TOGGLE                                       */}
          {/* ================================================================ */}
          <div className="mb-8 p-6 bg-gradient-to-r from-sky-50 to-cyan-50 border-2 border-sky-200 rounded-xl">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-sky-100 rounded-lg">
                    <svg className="w-6 h-6 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {t('simpleOrders.toggle.title', 'Simple Orders Mode')}
                  </h3>
                  {formData.simpleOrdersEnabled && (
                    <span className="px-3 py-1 bg-sky-600 text-white text-xs font-semibold rounded-full">
                      {t('simpleOrders.toggle.enabled', 'Enabled')}
                    </span>
                  )}
                </div>

                <p className="text-sm text-gray-700 mb-3">
                  {t('simpleOrders.toggle.helper', 'Collapses new orders into a placed → completed flow. Item-level statuses, multi-stage transitions, and full modification history are hidden.')}
                </p>

                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li className="flex items-start">
                    <svg className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>{t('simpleOrders.toggle.kept', 'Cancel, Excel export, per-order discount, light edit (qty/remove), and invoice upload remain available.')}</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-4 h-4 text-amber-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59c.75 1.334-.213 2.98-1.742 2.98H3.482c-1.53 0-2.493-1.646-1.743-2.98L8.257 3.099zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span>{t('simpleOrders.toggle.dropped', 'Item-level status (modified / out_of_stock / removed) and the full modification-history workflow are hidden.')}</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-4 h-4 text-gray-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <span>{t('simpleOrders.toggle.offHelper', 'When OFF, future orders return to the full workflow.')}</span>
                  </li>
                </ul>

                {erpActive && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-900">
                      {t('simpleOrders.toggle.erpHeadsUp', 'Heads up — Simple Orders work alongside SoftOne, but light edits on the platform aren\'t pushed back to your ERP. Keep both in sync manually if you edit on either side.')}
                    </p>
                  </div>
                )}
              </div>

              <div className="ml-6">
                <button
                  type="button"
                  onClick={() => handleSimpleOrdersToggle(!formData.simpleOrdersEnabled)}
                  disabled={saving}
                  className={`relative inline-flex h-10 w-20 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    formData.simpleOrdersEnabled ? 'bg-sky-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-9 w-9 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      formData.simpleOrdersEnabled ? 'translate-x-10' : 'translate-x-0'
                    }`}
                  />
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  {formData.simpleOrdersEnabled
                    ? t('simpleOrders.toggle.on', 'ON')
                    : t('simpleOrders.toggle.off', 'OFF')}
                </p>
              </div>
            </div>
          </div>

          {/* ================================================================ */}
          {/* ENTERPRISE STOREFRONT SETTINGS                                  */}
          {/* ================================================================ */}
          {subscriptionTier === 'enterprise' && (
          <div className="mb-8 p-6 bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200 rounded-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-amber-100 rounded-lg">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">{t('storefront.title', 'Storefront Settings')}</h3>
                <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Enterprise</span>
              </div>
            </div>

            {/* Custom URL Slug */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('storefront.customUrl', 'Custom URL')}
              </label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 whitespace-nowrap">wholesalehub.gr/wholesalers/</span>
                <input
                  type="text"
                  value={customSlug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  placeholder="your-brand-name"
                  className={`flex-1 p-2 border-2 rounded-lg text-sm ${
                    slugAvailable === true ? 'border-green-400 bg-green-50' :
                    slugAvailable === false ? 'border-red-400 bg-red-50' :
                    'border-gray-200'
                  }`}
                />
                <button
                  onClick={handleSaveSlug}
                  disabled={saving || (customSlug.length >= 3 && slugAvailable === false)}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-50"
                >
                  {t('common.save', 'Save')}
                </button>
              </div>
              <div className="mt-1 text-xs">
                {slugChecking && <span className="text-gray-400">{t('storefront.checking', 'Checking availability...')}</span>}
                {slugAvailable === true && <span className="text-green-600">✓ {t('storefront.available', 'Available!')}</span>}
                {slugAvailable === false && <span className="text-red-600">✗ {t('storefront.taken', 'This URL is already taken')}</span>}
                {!slugChecking && slugAvailable === null && customSlug.length > 0 && customSlug.length < 3 && (
                  <span className="text-gray-400">{t('storefront.minChars', 'Minimum 3 characters')}</span>
                )}
              </div>
              {customSlug && customSlug.length >= 3 && slugAvailable !== false && (
                <p className="mt-2 text-xs text-amber-700 bg-amber-100 inline-block px-2 py-1 rounded">
                  🔗 wholesalehub.gr/wholesalers/{customSlug}
                </p>
              )}
            </div>

            {/* Toggle: Hide from Marketplace */}
            <div className="flex items-center justify-between py-3 border-t border-amber-200">
              <div>
                <p className="text-sm font-medium text-gray-900">{t('storefront.hideFromMarketplace', 'Hide from Marketplace')}</p>
                <p className="text-xs text-gray-500">{t('storefront.hideFromMarketplaceDesc', 'Businesses can only find you via your direct URL')}</p>
              </div>
              <button
                type="button"
                onClick={() => handleStorefrontToggle('hideFromMarketplace', !hideFromMarketplace)}
                disabled={saving}
                className={`relative inline-flex h-8 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
                  hideFromMarketplace ? 'bg-amber-600' : 'bg-gray-300'
                } disabled:opacity-50`}
              >
                <span className={`pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow transition duration-200 ${
                  hideFromMarketplace ? 'translate-x-6' : 'translate-x-0'
                }`} />
              </button>
            </div>

            {/* Toggle: Hide Pricing */}
            <div className="flex items-center justify-between py-3 border-t border-amber-200">
              <div>
                <p className="text-sm font-medium text-gray-900">{t('storefront.hidePricing', 'Hide Prices from Businesses')}</p>
                <p className="text-xs text-gray-500">{t('storefront.hidePricingDesc', 'Prices won\'t be visible to businesses. Orders are still tracked with full pricing on your side.')}</p>
              </div>
              <button
                type="button"
                onClick={() => handleStorefrontToggle('hidePricing', !hidePricing)}
                disabled={saving}
                className={`relative inline-flex h-8 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
                  hidePricing ? 'bg-amber-600' : 'bg-gray-300'
                } disabled:opacity-50`}
              >
                <span className={`pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow transition duration-200 ${
                  hidePricing ? 'translate-x-6' : 'translate-x-0'
                }`} />
              </button>
            </div>
          </div>
          )}

          {/* ================================================================ */}
          {/* ERP CONNECTIVITY (Enterprise only)                              */}
          {/* ================================================================ */}
          {subscriptionTier === 'enterprise' && (
            <ErpConnectivitySection />
          )}

          {/* Delivery Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('profile.minimumOrder')}
              </label>
              <input
                type="number"
                name="minimumOrder"
                value={formData.minimumOrder}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('profile.deliveryFee')}
              </label>
              <input
                type="number"
                name="deliveryFee"
                value={formData.deliveryFee}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('profile.freeDeliveryThreshold')}
              </label>
              <input
                type="number"
                name="freeDeliveryThreshold"
                value={formData.freeDeliveryThreshold}
                onChange={handleChange}
                min="0"
                step="0.01"
                placeholder="0"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('profile.estimatedDeliveryDays')}
              </label>
              <input
                type="number"
                name="estimatedDeliveryDays"
                value={formData.estimatedDeliveryDays}
                onChange={handleChange}
                min="1"
                max="30"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('profile.deliveryNotes')}
            </label>
            <textarea
              name="deliveryNotes"
              value={formData.deliveryNotes}
              onChange={handleChange}
              rows="3"
              placeholder={t('profile.deliveryNotesPlaceholder')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? t('profile.saving') : t('profile.save')}
            </button>
          </div>
        </form>

        {/* Password Change Form */}
        <form onSubmit={handlePasswordChange} className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">{t('profile.changePassword')}</h2>

          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('profile.currentPassword')}
              </label>
              <PasswordField
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('profile.newPassword')}
              </label>
              <PasswordField
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('profile.confirmPassword')}
              </label>
              <PasswordField
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="bg-gray-800 text-white px-6 py-2 rounded-lg hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? t('profile.saving') : t('profile.changePassword')}
            </button>
          </div>
        </form>
      </main>
      <Footer />
    </div>
  );
};

export default WholesalerProfile;